import { useEffect, useState } from 'react';
import { useNotices, NOTICE_SLOTS, SiteNotice } from '@/hooks/useNotices';

interface NoticeManagerProps {
  canEdit: boolean;
}

export default function NoticeManager({ canEdit }: NoticeManagerProps) {
  const {
    activeBySlot,
    history,
    loading,
    error,
    saving,
    saveNotice,
    toggleActive,
    restoreNotice,
    reload,
  } = useNotices();

  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const next: Record<number, string> = {};
    NOTICE_SLOTS.forEach((slot) => {
      next[slot] = activeBySlot[slot]?.text ?? '';
    });
    setDrafts(next);
  }, [activeBySlot]);

  const handleSave = async (slot: number) => {
    setMessage(null);
    try {
      await saveNotice(slot, drafts[slot] ?? '');
      setMessage('공지가 저장됐어요.');
      setTimeout(() => setMessage(null), 2500);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : '저장에 실패했어요.');
    }
  };

  const handleToggle = async (notice: SiteNotice) => {
    setMessage(null);
    try {
      await toggleActive(notice.id, !notice.is_active);
      setMessage(notice.is_active ? '비활성화했어요.' : '활성화했어요.');
      setTimeout(() => setMessage(null), 2500);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : '변경에 실패했어요.');
    }
  };

  const handleRestore = async (notice: SiteNotice) => {
    setMessage(null);
    try {
      await restoreNotice(notice.id, notice.slot);
      setMessage('공지를 복원했어요.');
      setTimeout(() => setMessage(null), 2500);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : '복원에 실패했어요.');
    }
  };

  return (
    <section>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-stone-900">상단 공지 (Notice)</h2>
        <p className="text-sm text-stone-500 mt-1">
          사이트 최상단 바에서 흐르는 공지 문구예요. 슬롯별로 하나씩 활성화되며, 수정하면 이전 문구는 히스토리로 남아요.
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-5 py-4 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-600 text-lg" />
          <p className="text-sm text-red-700 flex-1">공지를 불러오지 못했어요: {error}</p>
          <button
            type="button"
            onClick={() => reload()}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-red-300 text-red-700 hover:bg-red-100 transition-colors whitespace-nowrap"
          >
            다시 시도
          </button>
        </div>
      )}

      {message && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-5 py-3 flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-green-600" />
          <span className="text-sm text-green-700">{message}</span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-stone-400">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
            <span className="text-sm">불러오는 중...</span>
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {NOTICE_SLOTS.map((slot) => {
              const active = activeBySlot[slot];
              const isActive = Boolean(active);
              return (
                <div key={slot} className="p-5">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-stone-900 whitespace-nowrap">
                        Notice {slot}
                      </span>
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${
                          isActive
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-stone-100 text-stone-500 border-stone-200'
                        }`}
                      >
                        {isActive ? '활성' : '비활성'}
                      </span>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isActive}
                        disabled={!canEdit || saving || !active}
                        onChange={() => active && handleToggle(active)}
                        className="w-4 h-4 text-amber-600 rounded border-stone-300 focus:ring-amber-500"
                      />
                      <span className="text-xs text-stone-500 whitespace-nowrap">활성화</span>
                    </label>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      value={drafts[slot] ?? ''}
                      disabled={!canEdit || saving}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [slot]: e.target.value }))
                      }
                      placeholder="공지 문구를 입력하세요"
                      className="flex-1 px-4 py-2 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors disabled:bg-stone-50 disabled:text-stone-400"
                    />
                    <button
                      type="button"
                      disabled={!canEdit || saving || !(drafts[slot] ?? '').trim()}
                      onClick={() => handleSave(slot)}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                      {saving ? (
                        <i className="ri-loader-4-line animate-spin" />
                      ) : (
                        <i className="ri-save-line" />
                      )}
                      저장
                    </button>
                  </div>

                  {active && (
                    <p className="text-xs text-stone-400 mt-2">
                      마지막 수정: {new Date(active.updated_at).toLocaleString()}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* History */}
      <div className="mt-6">
        <h3 className="text-sm font-semibold text-stone-700 mb-3">
          이전 공지 히스토리{' '}
          <span className="text-stone-400 font-normal">({history.length})</span>
        </h3>
        {history.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-200 px-5 py-8 text-center text-sm text-stone-400">
            아직 이전 공지가 없어요.
          </div>
        ) : (
          <div className="space-y-2">
            {history.map((notice) => (
              <div
                key={notice.id}
                className="bg-white rounded-xl border border-stone-200 px-4 py-3 flex items-center justify-between gap-3"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <span className="shrink-0 mt-0.5 inline-flex px-2 py-0.5 text-xs font-medium rounded-md bg-stone-100 text-stone-500 whitespace-nowrap">
                    Notice {notice.slot}
                  </span>
                  <p className="text-sm text-stone-600 break-words">{notice.text}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-stone-400 whitespace-nowrap">
                    {new Date(notice.updated_at).toLocaleDateString()}
                  </span>
                  <button
                    type="button"
                    disabled={!canEdit || saving}
                    onClick={() => handleRestore(notice)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors whitespace-nowrap"
                  >
                    <i className="ri-history-line" />
                    복원
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}